/**
 * SeasonSystem — Spring, Summer, Fall, Winter cycle.
 *
 * NOT IN MINECRAFT.
 *
 * Each season lasts 20 in-game days (~6.5 hours real-time).
 * Seasons affect:
 * - Leaf colors (spring green, summer bright, fall orange/red, winter white)
 * - Day length (winter shorter, summer longer)
 * - Mob spawn rates
 * - Crop growth speed
 * - Weather chances (winter = more snow, summer = more rain)
 * - Ambient music
 * - UI accent color
 */

const SEASONS = [
  {
    id: 'spring', name: 'Primavera', icon: '🌸',
    leafTint: 0x7FC858,     // bright green
    accent: '#E91E63',      // pink
    dayLengthMult: 1.0,
    cropGrowthMult: 1.3,    // crops grow 30% faster
    weatherRain: 0.4, weatherSnow: 0.05,
    mobSpawnMult: 1.0,
  },
  {
    id: 'summer', name: 'Verano', icon: '☀️',
    leafTint: 0x4CAF50,     // deep vibrant green
    accent: '#FFC107',      // gold
    dayLengthMult: 1.3,     // longer days
    cropGrowthMult: 1.5,    // +50% growth
    weatherRain: 0.2, weatherSnow: 0,
    mobSpawnMult: 1.2,      // more mobs
  },
  {
    id: 'fall', name: 'Otoño', icon: '🍂',
    leafTint: 0xE67E22,     // orange-brown
    accent: '#FF6F00',      // deep orange
    dayLengthMult: 0.9,
    cropGrowthMult: 1.0,
    weatherRain: 0.35, weatherSnow: 0.1,
    mobSpawnMult: 1.1,
  },
  {
    id: 'winter', name: 'Invierno', icon: '❄️',
    leafTint: 0xC8D8E8,     // frosted pale blue
    accent: '#03A9F4',      // ice blue
    dayLengthMult: 0.7,     // short days
    cropGrowthMult: 0.5,    // crops grow SLOW
    weatherRain: 0.1, weatherSnow: 0.5,  // mostly snow
    mobSpawnMult: 0.7,      // fewer mobs
  },
];

const DAYS_PER_SEASON = 20;
const STORAGE_KEY = 'fedeworld_season';

export class SeasonSystem {
  constructor() {
    this.currentSeasonIndex = 0;
    this.dayInSeason = 0;
    this._lastKnownDay = 0;
    this._load();

    // Notifications callback
    this.onSeasonChange = null;

    this._buildUI();
  }

  _load() {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      this.currentSeasonIndex = saved.seasonIndex ?? 0;
      this.dayInSeason = saved.dayInSeason ?? 0;
      this._lastKnownDay = saved.lastKnownDay ?? 0;
    } catch {}
  }

  _save() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      seasonIndex: this.currentSeasonIndex,
      dayInSeason: this.dayInSeason,
      lastKnownDay: this._lastKnownDay,
    }));
  }

  _buildUI() {
    // Season indicator top-left corner
    this._indicator = document.createElement('div');
    this._indicator.style.cssText = `
      position: fixed; top: 10px; left: 160px;
      background: rgba(0,0,0,0.6); padding: 6px 14px;
      font-family: 'Courier New', monospace; font-size: 13px;
      color: #FFF; border-left: 4px solid #4CAF50;
      z-index: 150; display: flex; align-items: center; gap: 8px;
      transition: border-color 0.6s;
    `;
    document.body.appendChild(this._indicator);
    this._updateIndicator();
  }

  _updateIndicator() {
    const s = this.getSeason();
    this._indicator.innerHTML = `
      <span style="font-size:18px;">${s.icon}</span>
      <span><b>${s.name}</b></span>
      <span style="color:#AAA;font-size:11px;">Día ${this.dayInSeason + 1}/${DAYS_PER_SEASON}</span>
    `;
    this._indicator.style.borderLeftColor = s.accent;
  }

  getSeason() {
    return SEASONS[this.currentSeasonIndex];
  }

  /**
   * Call when day advances.
   */
  onDayChange() {
    this._lastKnownDay++;
    this.dayInSeason++;
    if (this.dayInSeason >= DAYS_PER_SEASON) {
      this.dayInSeason = 0;
      this.currentSeasonIndex = (this.currentSeasonIndex + 1) % SEASONS.length;
      if (this.onSeasonChange) this.onSeasonChange(this.getSeason());
    }
    this._updateIndicator();
    this._save();
  }

  /**
   * Called every frame by main.js. Detects day changes.
   */
  update(dt, dayNight) {
    if (!dayNight || !dayNight.getDay) return;
    const currentDay = dayNight.getDay();
    if (currentDay > this._lastKnownDay) {
      this.onDayChange();
    }
  }

  getCropGrowthMult() { return this.getSeason().cropGrowthMult; }
  getMobSpawnMult() { return this.getSeason().mobSpawnMult; }
  getWeatherChances() {
    const s = this.getSeason();
    return { rain: s.weatherRain, snow: s.weatherSnow };
  }
}
